import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'

const HEARTBEAT_MS = 30_000

/**
 * 실제 재생 상태만 익명 운영 통계로 전달합니다.
 *
 * 서버는 sessionId를 메모리에만 유지하고 DB에는 곡/날짜별 합계만 저장합니다.
 * telemetry 실패는 연습 기능을 막지 않으며 운영 UI에도 기술 오류를 노출하지 않습니다.
 */
export function usePracticeTelemetry(shareId: string | null, playing: boolean): void {
  const [sessionId, setSessionId] = useState<string | null>(null)
  const desiredPlayingRef = useRef(playing)
  const currentShareIdRef = useRef(shareId)
  const startPendingRef = useRef(false)
  const sessionShareIdRef = useRef<string | null>(null)

  desiredPlayingRef.current = playing
  currentShareIdRef.current = shareId

  useEffect(() => {
    if (sessionId && sessionShareIdRef.current !== shareId) {
      const closing = sessionId
      sessionShareIdRef.current = null
      setSessionId(null)
      void api.practiceTelemetryStop(closing).catch(() => undefined)
      return
    }
    if (!playing || !shareId || sessionId || startPendingRef.current) return

    let cancelled = false
    startPendingRef.current = true
    void api.practiceTelemetryStart(shareId)
      .then((result) => {
        if (cancelled || !desiredPlayingRef.current || currentShareIdRef.current !== shareId) {
          void api.practiceTelemetryStop(result.sessionId).catch(() => undefined)
          return
        }
        sessionShareIdRef.current = shareId
        setSessionId(result.sessionId)
      })
      .catch(() => {
        if (import.meta.env.DEV) console.debug('[mioSegno][telemetry] practice start skipped')
      })
      .finally(() => {
        startPendingRef.current = false
      })

    return () => {
      cancelled = true
    }
  }, [playing, sessionId, shareId])

  useEffect(() => {
    if (playing || !sessionId) return
    const closing = sessionId
    sessionShareIdRef.current = null
    setSessionId(null)
    void api.practiceTelemetryStop(closing).catch(() => undefined)
  }, [playing, sessionId])

  useEffect(() => {
    if (!playing || !sessionId) return
    const timer = window.setInterval(() => {
      void api.practiceTelemetryHeartbeat(sessionId).catch(() => undefined)
    }, HEARTBEAT_MS)
    return () => window.clearInterval(timer)
  }, [playing, sessionId])

  useEffect(() => {
    return () => {
      // 브라우저 종료 시 비동기 stop이 전달되지 않아도 서버의 현재 세션 판정은 75초 뒤 자동 만료됩니다.
      // 일반적인 React 화면 전환에서는 best-effort stop을 전송합니다.
      if (sessionId) void api.practiceTelemetryStop(sessionId).catch(() => undefined)
    }
  }, [sessionId])
}
